import { Component } from '@theme/component'
import { releaseScrollLock, scrollLock } from '@theme/events'

const CLOSE_DELAY_MS = 200
const HEADER_SELECTOR = 'sticky-header'
const OPEN_ATTR = 'data-open'
const TOP_VAR = '--mega-menu-top'

class MegaMenu extends Component {
  requiredRefs = ['panel']
  timeout = null
  isEditorMode = false
  trigger = null
  triggerLink = null
  #controller = null

  setTriggerActive = (isActive) => {
    if (!this.triggerLink) return

    this.triggerLink.classList.toggle('alt', isActive)
    this.triggerLink.setAttribute('aria-expanded', isActive ? 'true' : 'false')
  }

  cancelClose = () => {
    clearTimeout(this.timeout)
    this.timeout = null
  }

  scheduleClose = () => {
    if (this.isEditorMode || this.timeout) return

    this.timeout = setTimeout(() => {
      this.timeout = null
      this.close()
    }, CLOSE_DELAY_MS)
  }

  updatePosition = () => {
    const header = document.querySelector(HEADER_SELECTOR)
    if (!(this.refs.panel && header)) return

    const top = Math.round(header.getBoundingClientRect().bottom)
    this.refs.panel.style.setProperty(TOP_VAR, `${top}px`)
  }

  open = () => {
    this.cancelClose()
    if (!this.refs.panel || this.isOpen) return

    this.updatePosition()
    this.refs.panel.setAttribute(OPEN_ATTR, '')
    this.setTriggerActive(true)
    scrollLock(true)
  }

  close = () => {
    if (!this.isOpen) return

    if (this.refs.panel.contains(document.activeElement)) {
      this.triggerLink?.focus({ preventScroll: true })
    }
    this.refs.panel.removeAttribute(OPEN_ATTR)
    this.setTriggerActive(false)
    releaseScrollLock()
  }

  onPointerEnter = () => {
    if (this.isEditorMode) return

    this.open()
  }

  onPointerLeave = () => {
    this.scheduleClose()
  }

  onTriggerFocusIn = () => {
    if (this.isEditorMode) return

    this.open()
  }

  onFocusOut = (event) => {
    if (this.keepsFocusNearby(event.relatedTarget)) return

    this.scheduleClose()
  }

  keepsFocusNearby = (target) => {
    const header = document.querySelector(HEADER_SELECTOR)
    const isInside =
      header?.contains(target) ||
      this.trigger?.contains(target) ||
      this.refs.panel?.contains(target)
    const isHovered =
      this.trigger?.matches(':hover') || this.refs.panel?.matches(':hover')
    return Boolean(isInside || isHovered)
  }

  onDocumentKeyDown = (event) => {
    if (this.isEditorMode) return

    if (event.key === 'Escape' && this.isOpen) {
      this.close()
    }
  }

  resolveTrigger = () => {
    const defaultTrigger = document.querySelector(this.getAttribute('trigger'))
    const triggerTitle = this.dataset.triggerTitle
    if (!triggerTitle) return defaultTrigger

    const defaultLink = defaultTrigger?.querySelector('.link')
    defaultLink?.removeAttribute('aria-expanded')
    defaultLink?.removeAttribute('aria-controls')
    return [...document.querySelectorAll('.header__nav-item')].find(
      (item) => item.dataset.menuTitle === triggerTitle
    )
  }

  get isOpen() {
    return this.refs.panel?.hasAttribute(OPEN_ATTR) ?? false
  }

  connectedCallback() {
    super.connectedCallback()

    this.trigger = this.resolveTrigger()
    this.triggerLink = this.trigger?.querySelector?.('.link')
    this.triggerLink?.setAttribute('aria-controls', this.refs.panel.id)
    this.setTriggerActive(this.isOpen)

    this.#controller = new AbortController()
    this.#bindEvents(this.#controller.signal)
  }

  disconnectedCallback() {
    this.#controller?.abort()
    this.#controller = null
    this.cancelClose()
    this.close()

    super.disconnectedCallback()
  }

  #bindEvents(signal) {
    const { trigger } = this
    trigger?.addEventListener('mouseenter', this.onPointerEnter, { signal })
    trigger?.addEventListener('mouseleave', this.onPointerLeave, { signal })
    trigger?.addEventListener('focusin', this.onTriggerFocusIn, { signal })
    trigger?.addEventListener('focusout', this.onFocusOut, { signal })

    const { panel } = this.refs
    panel?.addEventListener('mouseenter', this.onPointerEnter, { signal })
    panel?.addEventListener('mouseleave', this.onPointerLeave, { signal })
    panel?.addEventListener('focusout', this.onFocusOut, { signal })

    const header = document.querySelector(HEADER_SELECTOR)
    header?.addEventListener('focusout', this.onFocusOut, { signal })

    document.addEventListener('keydown', this.onDocumentKeyDown, { signal })
  }
}

if (!customElements.get('mega-menu')) {
  customElements.define('mega-menu', MegaMenu)
}
